/**
 * WordPress dependencies
 */
import { CheckboxControl } from '@safe-wordpress/components';
import { useSelect } from '@safe-wordpress/data';
import { useEffect } from '@safe-wordpress/element';
import { _x } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import clsx from 'clsx';

/**
 * Internal dependencies
 */
import { useExperimentAttribute, useExperimentType } from '../../../hooks';
import { store as NAB_EDITOR } from '../../../../store';
import './style.scss';

export const AutoApplyWinner = (): JSX.Element | null => {
	const [ isAutoApplyEnabled, enableAutoApply ] = useExperimentAttribute(
		'autoAlternativeApplication'
	);
	const supportsAutoApply = useSupportsAlternativeApplication();

	useEffect( () => {
		if ( 'yes' === supportsAutoApply ) {
			return;
		}
		enableAutoApply( false );
	}, [ supportsAutoApply, enableAutoApply ] );

	if ( 'no' === supportsAutoApply ) {
		return null;
	}

	return (
		<div className="nab-experiment-auto-apply-winner">
			<CheckboxControl
				className={ clsx( {
					'nab-experiment-auto-apply-winner__checkbox': true,
					'nab-experiment-auto-apply-winner__checkbox--disabled':
						'not-now' === supportsAutoApply,
				} ) }
				label={ _x(
					'Apply winner automatically',
					'command',
					'nelio-ab-testing'
				) }
				checked={ isAutoApplyEnabled }
				onChange={ enableAutoApply }
				disabled={ 'not-now' === supportsAutoApply }
			/>
		</div>
	);
};

// =====
// HOOKS
// =====

const useSupportsAlternativeApplication = (): 'yes' | 'no' | 'not-now' => {
	const type = useExperimentType();
	const control = useSelect(
		( select ) => select( NAB_EDITOR ).getAlternative( 'control' ),
		[]
	);

	if ( ! control || ! type?.supports.alternativeApplication ) {
		return 'no';
	}

	if ( 'boolean' === typeof type.supports.alternativeApplication ) {
		return type.supports.alternativeApplication ? 'yes' : 'no';
	}

	return type.supports.alternativeApplication( control.attributes )
		? 'yes'
		: 'not-now';
};
